import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { LiquidDialog, useLiquidFeedback } from './LiquidUI';
import { OrcamentoIcon } from './OrcamentoUI';

function DocumentoEncomenda({ o, documentos }) {
    const [indice, setIndice] = useState(0);
    const [url, setUrl] = useState('');
    const [erro, setErro] = useState('');
    const doc = documentos[indice];
    useEffect(() => {
        let ativo = true, ficheiroUrl = ''; setUrl(''); setErro('');
        if (!doc) return;
        api.get('/orcamentos/' + o.id + '/download-ne', { params: { documento_id: doc.id }, responseType: 'blob' })
            .then(r => { ficheiroUrl = URL.createObjectURL(r.data); if (ativo) setUrl(ficheiroUrl); else URL.revokeObjectURL(ficheiroUrl); })
            .catch(() => { if (ativo) setErro('Não foi possível abrir o documento.'); });
        return () => { ativo = false; if (ficheiroUrl) URL.revokeObjectURL(ficheiroUrl); };
    }, [o.id, doc?.id]);
    if (!doc) return <p>Não existem documentos anexados.</p>;
    return <>
        {documentos.length > 1 && <select aria-label="Documento da encomenda" value={indice} onChange={e => setIndice(Number(e.target.value))}>{documentos.map((d, i) => <option key={d.id} value={i}>{i + 1} / {documentos.length} — {d.nome}</option>)}</select>}
        {erro ? <p role="alert">{erro}</p> : url ? <><iframe title={doc.nome} src={url} className="gf-ne-documento" /><a href={url} target="_blank" rel="noopener noreferrer">Abrir documento numa nova janela</a></> : <p>A abrir documento…</p>}
    </>;
}

export function useEncomendasCliente(setLista) {
    const feedback = useLiquidFeedback();
    const [anexando, setAnexando] = useState({});
    const [consulta, setConsulta] = useState(null);
    const [mensagem, setMensagem] = useState('');
    const bloqueio = useRef(false);
    const pedido = useRef(0);
    const atualizar = (o, data) => {
        pedido.current++;
        setLista(prev => prev.map(row => row.id === o.id ? { ...row, ne_ficheiro: data.ne_ficheiro, ne_documentos: data.documentos } : row));
        setConsulta(prev => prev?.o.id === o.id ? { ...prev, documentos: data.documentos, erro: '', loading: false } : prev);
    };
    const anexarEncomenda = async (o, files, documentoId) => {
        const lista = Array.from(files || []);
        if (!lista.length || bloqueio.current) return;
        if (lista.some(f => f.size > 20 * 1024 * 1024)) return feedback.notify('Cada documento pode ter no máximo 20 MB.');
        if (documentoId && !await feedback.confirm('Substituir apenas este documento? Os restantes serão mantidos.')) return;
        if (bloqueio.current) return;
        bloqueio.current = true;
        setAnexando({ [o.id]: true });
        let gravados = 0;
        try {
            // Confirma suporte a múltiplos anexos antes de enviar qualquer ficheiro.
            const suporte = await api.get('/orcamentos/' + o.id + '/encomendas-cliente');
            if (!Array.isArray(suporte.data.documentos)) throw new Error('Servidor ainda não atualizado');
            for (const file of lista) {
                const fd = new FormData();
                fd.append('ficheiro', file);
                if (documentoId) fd.append('documento_id', documentoId);
                const r = await api.post('/orcamentos/' + o.id + '/upload-ne', fd);
                atualizar(o, r.data);
                gravados++;
            }
            setMensagem(documentoId ? 'Documento substituído.' : gravados + ' documento(s) acrescentado(s).');
        } catch (e) {
            await feedback.notify((gravados ? gravados + ' documento(s) já acrescentado(s). ' : '') + (e.response?.data?.error || 'Não foi possível confirmar o carregamento. Consulte a lista antes de voltar a anexar.'));
        } finally { bloqueio.current = false; setAnexando({}); }
    };
    const abrirEncomenda = async o => {
        const token = ++pedido.current;
        setMensagem('');
        setConsulta({ o, documentos: [], loading: true, erro: '' });
        try {
            const r = await api.get('/orcamentos/' + o.id + '/encomendas-cliente');
            if (token === pedido.current) setConsulta({ o, documentos: r.data.documentos, loading: false, erro: '' });
        } catch (e) {
            // Compatibilidade de leitura enquanto a atualização do servidor está pendente.
            if ([404, 500].includes(e.response?.status)) {
                try {
                    const r = await api.get('/orcamentos/' + o.id);
                    if (r.data.id && r.data.ne_documentos == null) {
                        const caminho = r.data.ne_ficheiro;
                        const documentos = caminho ? [{ id: 'legado', nome: caminho.split('/').pop(), caminho }] : [];
                        if (token === pedido.current) setConsulta({ o, documentos, loading: false, erro: '', legado: true });
                        return;
                    }
                } catch { /* Mantém a mensagem de erro se a consulta também falhar. */ }
            }
            if (token === pedido.current) setConsulta({ o, documentos: [], loading: false, erro: 'Não foi possível carregar os documentos. Feche e volte a tentar.' });
        }
    };
    const visualizar = async (o, doc) => {
        const janela = window.open('about:blank', '_blank');
        if (!janela) return feedback.notify('Permita abrir uma nova janela para visualizar o documento.');
        janela.opener = null;
        try {
            const r = await api.get('/orcamentos/' + o.id + '/download-ne', { params: { documento_id: doc.id }, responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            if (janela.closed) { URL.revokeObjectURL(url); return; }
            janela.location.replace(url);
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch { janela.close(); feedback.notify('Não foi possível abrir o documento.'); }
    };
    const eliminar = async (o, doc) => {
        if (bloqueio.current || !await feedback.confirm('Eliminar "' + doc.nome + '" da lista de encomendas deste projeto?')) return;
        if (bloqueio.current) return;
        bloqueio.current = true;
        setAnexando({ [o.id]: true });
        try {
            const r = await api.delete('/orcamentos/' + o.id + '/upload-ne', { params: { documento_id: doc.id } });
            atualizar(o, r.data);
            setMensagem('Documento eliminado da lista.');
        } catch { feedback.notify('Não foi possível eliminar o documento.'); }
        finally { bloqueio.current = false; setAnexando({}); }
    };
    const fechar = () => { if (!bloqueio.current) { pedido.current++; setConsulta(null); } };
    const dialogo = consulta && (
        <LiquidDialog className="gf-modal-overlay" role="dialog" aria-modal="true" aria-label="Encomendas cliente" onClose={fechar} onClick={fechar}>
            <div className="gf-modal gf-ne-modal" onClick={e => e.stopPropagation()}>
                <div className="gf-modal-header">
                    <h3>Encomendas cliente · #{consulta.o.id}</h3>
                    <button className="gf-modal-close" type="button" aria-label="Fechar" disabled={!!anexando[consulta.o.id]} onClick={fechar}>×</button>
                </div>
                <div className="gf-modal-body">
                    {!consulta.loading && !consulta.erro && !consulta.gerir && <DocumentoEncomenda key={consulta.o.id} o={consulta.o} documentos={consulta.documentos} />}
                    {!consulta.loading && !consulta.erro && !consulta.gerir && !consulta.legado && <button type="button" className="btn-ver-orc" onClick={() => setConsulta(prev => ({ ...prev, gerir: true }))}>Gerir documentos</button>}
                    {consulta.loading ? <p>A carregar documentos…</p> : consulta.erro ? <p role="alert">{consulta.erro}</p> : consulta.gerir ? <>
                        {!consulta.documentos.length && <p>Ainda não existem documentos anexados.</p>}
                        <ul className="gf-ne-lista">
                            {consulta.documentos.map(doc => <li key={doc.id}>
                                <span className="gf-ne-nome">{doc.nome}</span>
                                <div className="gf-ne-acoes">
                                    <button type="button" className="btn-ver-orc" onClick={() => visualizar(consulta.o, doc)}>Ver</button>
                                    {!consulta.legado && <><label className="btn-ver-orc gf-encomenda-upload">Substituir
                                        <input type="file" aria-label={'Substituir ' + doc.nome} disabled={!!anexando[consulta.o.id]} onChange={e => { const files = Array.from(e.target.files); e.target.value = ''; anexarEncomenda(consulta.o, files, doc.id); }} />
                                    </label>
                                    <button type="button" className="btn-ver-orc" disabled={!!anexando[consulta.o.id]} onClick={() => eliminar(consulta.o, doc)}>Eliminar</button></>}
                                </div>
                            </li>)}
                        </ul>
                        {consulta.legado ? <p>A consulta está disponível. Para acrescentar ou gerir vários documentos, é necessário concluir a atualização do servidor.</p> : <EncomendaCliente o={consulta.o} anexando={anexando} anexarEncomenda={anexarEncomenda} />}
                    </> : null}
                    <p role="status">{mensagem}</p>
                </div>
            </div>
        </LiquidDialog>
    );
    return { anexando, anexarEncomenda, abrirEncomenda, dialogo };
}

export function EncomendaCliente({ o, anexando, anexarEncomenda }) {
    return <div className="gf-encomenda-controlos">
        <label className="btn-ver-orc gf-encomenda-upload">
            <OrcamentoIcon name="upload" fallback="↑" /> {anexando[o.id] ? 'A carregar…' : 'Anexar'}
            <input type="file" multiple aria-label={'Acrescentar encomendas cliente ao orçamento ' + o.id} disabled={!!anexando[o.id]} onChange={e => { const files = Array.from(e.target.files); e.target.value = ''; anexarEncomenda(o, files); }} />
        </label>
    </div>;
}
